import { useState, useCallback, useMemo } from 'react';

import { TimedAction } from './timed_action';

/**
 * Checks if given string has POTENTIAL for becoming a valid numeric value. This is very useful for
 * validating text input fields when a complete number has not been entered yet:
 *
 * The following values are all valid
 * "", "1", "1.", "1.2", "1.23"
 * " ", " 1", " 1.", " 1.23", " 1.23 "
 * "-", "-1", "-1.", "-1.2", "-1.23"
 * "+", "+1", "+1.", "+1.2", "+1.23"
 * ".", ".1"
 *
 * The following values are invalid
 * "hello"
 * "1+23", "12+3", "123+", "1-23"
 * "1.23."
 */
export function canBeNumericString(value: string): boolean {
  value = value.trim();
  // Only 0-9, period, plus, minus allowed
  if (/[^0-9.+-]/.test(value)) return false;
  // Sign cannot appear anywhere except start
  if (/.+[+-]/.test(value)) return false;
  // Period cannot appear multiple times
  if (/[.].*[.]/.test(value)) return false;
  return true;
}

export function makeNumberFormatter(precision: number, useGrouping: boolean, forceDecimalPlaces: boolean = false) {
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: precision,
    minimumFractionDigits: forceDecimalPlaces ? precision : 0,
    notation: 'standard', // Plain number formatting
    // roundingIncrement: 25, // Snap to [x.00], [x.25], [x.50], [x.75], [x+1.00]
    roundingMode: 'trunc', // Truncate fractions [x.999] -> [x.99]
    signDisplay: 'auto', // Show sign for negative numbers only (including -0),
    style: 'decimal',
    useGrouping: useGrouping ? 'auto' : false
  } as any); // Any needed since typescript declaration does not include experimental feature options (roundingMode, useGrouping)
  return formatter;
}

export function formatNumberToPrecision(value: number, precision: number, forceDecimalPlaces: boolean = false): string {
  return makeNumberFormatter(precision, false, forceDecimalPlaces).format(value);
}

export function formatNumberToPrecisionWithCommas(value: number, precision: number, forceDecimalPlaces: boolean = false): string {
  return makeNumberFormatter(precision, true, forceDecimalPlaces).format(value);
}

export type InputConstraintsResult = {
  inputTextValue: string; // Text value entered by user
  inputNumericValue: number; // Numeric value for above
  constrainedTextValue: string; // Constrained text value
  constrainedNumericValue: number; // Constrained numeric value for above
  hasInvalidCharacters: boolean; // If string contains characters that cannot be parsed as a number
  isMinTriggered: boolean; // If min check was triggered
  isMaxTriggered: boolean; // If max check was triggered
  isPrecisionTriggered: boolean; // If precision check was triggered
};

/**
 * Applies configured constraints on the given input string
 * @param inputTextValue Given input string.
 * @param precision Maximum acceptable precision. Values with more precision will be truncated down (not rounded) to this precision.
 * @param min Minimum acceptable value (optional). Values lower than this will be set to NaN.
 * @param max Maximum acceptable value (optional). Values higher than this will be set to NaN.
 * @returns InputConstraintsResult object
 */
export function applyInputConstraints(inputTextValue: string, precision: number = 0, min?: number, max?: number): InputConstraintsResult {
  try {
    //
    // Compute multiplier needed to convert decimal value with valid precision to integer
    /**
     * If allowed precision is 2, then multiplier is 10^2 = 100. If we multiply a number by this
     * multiplier and it is still an integer, then the number has valid precision. Ex:
     * 1     x 100 = 100   (is integer, so 1 is OK)
     * 1.2   x 100 = 120   (is integer, so 1.2 is OK)
     * 1.23  x 100 = 123   (is integer, so 1.23 is OK)
     * 1.234 x 100 = 123.4 (not integer, so 1.234 is not OK)
     */
    const multiplier = Math.pow(10, precision);

    // Safety checks
    if (precision < 0 || !Number.isInteger(precision)) throw new Error('Precision needs to be a non-negative integer');
    if (min !== undefined && !Number.isInteger(min * multiplier)) throw new Error('Min needs to have proper precision');
    if (max !== undefined && !Number.isInteger(max * multiplier)) throw new Error('Max needs to have proper precision');
    if (min !== undefined && max !== undefined && min > max) throw new Error('Min needs to be lesser than Max');

    // Compute invalid characters flag
    const hasInvalidCharacters = !canBeNumericString(inputTextValue);

    // Parse input text as number
    const inputNumericValue = Number(inputTextValue);

    // If text is blank, or numeric value is invalid
    if (inputTextValue.trim() === '' || isNaN(inputNumericValue))
      return {
        inputTextValue,
        inputNumericValue,
        constrainedTextValue: '',
        constrainedNumericValue: NaN,
        hasInvalidCharacters,
        isMinTriggered: false,
        isMaxTriggered: false,
        isPrecisionTriggered: false
      };

    // Compute numeric value with valid precision
    // Use relative epsilon to avoid floating-point precision issues (e.g., 1234567.89 * 100 = 123456788.99999999)
    const product = Math.abs(inputNumericValue) * multiplier;
    const epsilon = product * Number.EPSILON * 10;
    const precisionValue = (Math.floor(product + epsilon) * (inputNumericValue < 0 ? -1 : +1)) / multiplier;

    // Compute min/max flags
    const isMinTriggered = min !== undefined && precisionValue < min;
    const isMaxTriggered = max !== undefined && precisionValue > max;
    // Compute precision flag
    const isPrecisionTriggered = inputNumericValue !== precisionValue;

    // Compute text with valid precision and remove trailing .000s
    const precisionText = formatNumberToPrecision(precisionValue, precision);

    return {
      inputTextValue,
      inputNumericValue,
      constrainedTextValue: isMinTriggered || isMaxTriggered ? '' : precisionText,
      constrainedNumericValue: isMinTriggered || isMaxTriggered ? NaN : precisionValue,
      hasInvalidCharacters,
      isMinTriggered,
      isMaxTriggered,
      isPrecisionTriggered
    };
  } catch (error) {
    return {
      inputTextValue,
      inputNumericValue: Number(inputTextValue),
      constrainedTextValue: inputTextValue,
      constrainedNumericValue: Number(inputTextValue),
      hasInvalidCharacters: false,
      isMinTriggered: false,
      isMaxTriggered: false,
      isPrecisionTriggered: false
    };
  }
}

type InputConstraintOptions = {
  value: number | string; // Default numeric value to be rendered
  precision?: number; // Allowed precision for numeric value
  min?: number; // Minimum value
  max?: number; // Maximum value
  defaultTooltipText?: string; // Tooltip to be rendered by default
  invalidCharactersTooltipText?: string; // Tooltip to be rendered when input has invalid characters
  rangeTooltipText?: string; // General tooltip to be rendered when value is outside range
  minRangeTooltipText?: string; // Tooltip to be rendered when value is outside min range
  maxRangeTooltipText?: string; // Tooltip to be rendered when value is outside max range
  precisionTooltipText?: string; // Tooltip to be rendered when precision is bad
};

export function useInputConstraints({ value, precision = 0, min, max, defaultTooltipText, invalidCharactersTooltipText, rangeTooltipText, minRangeTooltipText, maxRangeTooltipText, precisionTooltipText }: InputConstraintOptions) {
  //
  const [inputTextValue, setInputTextValue] = useState(value.toString());
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [hasFocus, setFocus] = useState(false);
  const [inputConstraintsResult, setInputConstraintsResult] = useState(applyInputConstraints(inputTextValue, precision, min, max));

  /**
   * HACK for MUI
   *
   * We cannot rely on [tooltipText] alone to render/hide tooltips alone. Since we're changing
   * tooltips on the fly, if we clear the tooltip, the UI will flicker: The tooltip text will
   * suddenly disappear and its width changes as fade-out animation kicks in.
   *
   * So we need to keep track of an additional [isTooltipOpen] as well and ensure that when
   * the tooltip is cleared, we clear the [isTooltipOpen] flag first, then wait for some time
   * before clearing its text as well.
   */
  const clearTooltipTextAction = useMemo(() => new TimedAction(() => setTooltipText(''), 250), []);

  // Main heavy-lifting function to apply constraints and update relevant states
  const applyInputConstraintsWithEffects = useCallback(
    (text: string) => {
      const newResult = applyInputConstraints(text, precision, min, max);
      setInputConstraintsResult(newResult);
      setInputTextValue(newResult.inputTextValue);
      if (newResult.hasInvalidCharacters && invalidCharactersTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(invalidCharactersTooltipText);
      } else if ((newResult.isMinTriggered || newResult.isMaxTriggered) && rangeTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(rangeTooltipText);
      } else if (newResult.isMinTriggered && minRangeTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(minRangeTooltipText);
      } else if (newResult.isMaxTriggered && maxRangeTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(maxRangeTooltipText);
      } else if (newResult.isPrecisionTriggered && precisionTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(precisionTooltipText);
      } else if (defaultTooltipText) {
        setTooltipOpen(true);
        clearTooltipTextAction.cancel();
        setTooltipText(defaultTooltipText);
      } else {
        setTooltipOpen(false);
        clearTooltipTextAction.enqueue();
      }
      return newResult;
    },
    [precision, min, max, defaultTooltipText, invalidCharactersTooltipText, rangeTooltipText, minRangeTooltipText, maxRangeTooltipText, precisionTooltipText, clearTooltipTextAction]
  );

  // OnChange callback that caller code should hook up to TextField
  const onChangeCallback = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => applyInputConstraintsWithEffects(ev.target.value), [applyInputConstraintsWithEffects]);
  // OnFocus callback that caller code should hook up to TextField
  const onFocusCallback = useCallback(() => setFocus(true), []);
  // OnBlur callback that caller code should hook up to TextField
  const onBlurCallback = useCallback(() => setFocus(true), []);

  // Value setter that caller code can use to forcibly update TextField value
  const setValue = useCallback(
    (newValue: number | string) => {
      applyInputConstraintsWithEffects(newValue.toString());
    },
    [applyInputConstraintsWithEffects]
  );
  // Value resetter that caller code can use to revert TextField value back to initial value
  const revertValue = useCallback(() => applyInputConstraintsWithEffects(value.toString()), [value, applyInputConstraintsWithEffects]);

  return {
    inputTextValue: inputConstraintsResult.inputTextValue, // Caller should bind this value to TextField [value]
    // inputNumericValue: inputConstraintsResult.inputNumericValue,
    constrainedTextValue: inputConstraintsResult.constrainedTextValue, // Caller should use this if string value for number is required instead of number.
    constrainedNumericValue: inputConstraintsResult.constrainedNumericValue, // Caller should use this numeric value. NaN if value is not numeric, or outside range. Truncated to required precision already.
    // hasInvalidCharacters: inputConstraintsResult.hasInvalidCharacters,
    // isMinTriggered: inputConstraintsResult.isMinTriggered,
    // isMaxTriggered: inputConstraintsResult.isMaxTriggered,
    // isPrecisionTriggered: inputConstraintsResult.isPrecisionTriggered,
    hasFocus, // Caller can use this to show tooltips only when TextField has focus
    isTooltipOpen, // Caller should bind this to Tooltip [open]. Do not use [tooltipText] to show tooltips as it will lead to flickering
    tooltipText, // Caller should bind this to Tooltip [text]
    onChangeCallback, // Caller should bind (or chain) this to TextField [onChange]
    onFocusCallback, // Caller should bind (or chain) this to TextField [onFocus] if [hasFocus] needs to be used
    onBlurCallback, // Caller should bind (or chain) this to TextField [onBlur] if [hasFocus] needs to be used
    setValue, // Caller can use this to forcibly update TextField value (eg external update)
    revertValue // Caller can use this to revert TextField value to initial value (eg discard value when trying to commit NaN [inputNumericValue])
  };
}

export const RESOURCE_COUNT_MIN = 0.01;
export const RESOURCE_COUNT_MAX = 99999.99;
export const RESOURCE_COUNT_PRECISION = 2;

export const DURATION_MIN = 0;
export const DURATION_MAX = 999;
export const DURATION_MAX_TASK = 99;
export const DURATION_PRECISION = 0;

export const WORK_HOURS_MIN = 0;
export const WORK_HOURS_MAX = 24.0;
export const WORK_HOURS_PRECISION = 1;

export const ORG_FIELD_NUMBER_MIN = 0;
export const ORG_FIELD_NUMBER_PRECISION = 2;

export const LAG_MIN = -999;
export const LAG_MAX = 999;
export const LAG_PRECISION = 0;

export const RESOURCE_COUNT_TOOLTIP_DEFAULT = '';
export const RESOURCE_COUNT_TOOLTIP_FORMAT = 'Invalid characters';
export const RESOURCE_COUNT_TOOLTIP_RANGE = 'Range is 0.01 to 99,999.99';
export const RESOURCE_COUNT_TOOLTIP_PRECISION = 'Max two decimal places';

export const DURATION_TOOLTIP_DEFAULT = '';
export const DURATION_TOOLTIP_FORMAT = 'Invalid characters';
export const DURATION_TOOLTIP_RANGE = 'Range is 0 to 999';
export const DURATION_TOOLTIP_RANGE_FOR_TASK = 'Range is 0 to 99';
export const DURATION_TOOLTIP_PRECISION = 'Integer values allowed only';

export const LAG_TOOLTIP_DEFAULT = 'Lag';
export const LAG_TOOLTIP_FORMAT = 'Invalid characters';
export const LAG_TOOLTIP_RANGE = 'Range is -999 to 999';
export const LAG_TOOLTIP_PRECISION = 'Integer values allowed only';

export const WORK_HOURS_TOOLTIP_DEFAULT = '';
export const WORK_HOURS_TOOLTIP_FORMAT = 'Invalid characters';
export const WORK_HOURS_TOOLTIP_RANGE = 'Work Hours limit is 0 to 24.0';
export const WORK_HOURS_TOOLTIP_PRECISION = 'Max one decimal place';
